import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Alert, AlertDescription, AlertTitle } from '../../components/ui/alert';
import { Badge } from '../../components/ui/badge';
import api from '../../services/api';
import { toast } from 'sonner';
import {
  Settings as SettingsIcon,
  Save,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  MessageSquare,
  ArrowLeft,
  Eye,
  EyeOff,
  ExternalLink,
  TestTube2,
  Copy,
} from 'lucide-react';

export default function WhatsAppSettings() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [status, setStatus] = useState(null);      // GET /config response
  const [testResult, setTestResult] = useState(null);
  const [showSecrets, setShowSecrets] = useState(false);

  // Form state — only populated with user input (we never show existing secrets)
  const [form, setForm] = useState({
    phone_number_id: '',
    access_token: '',
    app_secret: '',
    verify_token: '',
    api_version: 'v20.0',
    business_account_id: '',
    webhook_url: '',
  });

  // Fallback: derive from backend URL only if admin hasn't set a custom webhook_url.
  const derivedWebhookUrl = `${((typeof window !== "undefined" && window.location ? window.location.origin : process.env.REACT_APP_BACKEND_URL) || window.location.origin).replace(/\/+$/, '')}/api/whatsapp/webhook`;
  const effectiveWebhookUrl = (status?.webhook_url && status.webhook_url.trim())
    ? status.webhook_url.trim()
    : derivedWebhookUrl;

  const loadStatus = async () => {
    try {
      const { data } = await api.get('/whatsapp/config');
      setStatus(data);
      // Pre-fill non-secret fields
      setForm((prev) => ({
        ...prev,
        phone_number_id: data.phone_number_id || '',
        verify_token: data.verify_token || '',
        api_version: data.api_version || 'v20.0',
        business_account_id: data.business_account_id || '',
        webhook_url: data.webhook_url || '',
      }));
    } catch (e) {
      toast.error('Failed to load WhatsApp config');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStatus();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      // Only send non-empty strings for secrets so we don't wipe them accidentally
      const payload = {};
      if (form.phone_number_id !== (status?.phone_number_id || '')) payload.phone_number_id = form.phone_number_id;
      if (form.verify_token !== (status?.verify_token || '')) payload.verify_token = form.verify_token;
      if (form.api_version !== (status?.api_version || '')) payload.api_version = form.api_version;
      if (form.business_account_id !== (status?.business_account_id || '')) payload.business_account_id = form.business_account_id;
      if (form.webhook_url !== (status?.webhook_url || '')) payload.webhook_url = form.webhook_url;
      if (form.access_token) payload.access_token = form.access_token;
      if (form.app_secret) payload.app_secret = form.app_secret;

      if (Object.keys(payload).length === 0) {
        toast.info('No changes to save');
        setSaving(false);
        return;
      }

      const { data } = await api.put('/whatsapp/config', payload);
      if (data.success) {
        toast.success('WhatsApp settings saved');
        // Clear secret inputs so they can't be accidentally re-sent
        setForm((prev) => ({ ...prev, access_token: '', app_secret: '' }));
        setTestResult(null);
        await loadStatus();
      } else {
        toast.error('Save failed');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const runTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post('/whatsapp/config/test');
      setTestResult(data);
      if (data.success) toast.success('Connection verified');
      else toast.error(data.error || 'Verification failed');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Test failed');
    } finally {
      setTesting(false);
    }
  };

  const clearField = async (field) => {
    setSaving(true);
    try {
      await api.put('/whatsapp/config', { [field]: '' });
      toast.success(`${field} cleared (falling back to .env)`);
      await loadStatus();
    } catch (e) {
      toast.error('Failed to clear');
    } finally {
      setSaving(false);
    }
  };

  const copyWebhook = async () => {
    try {
      await navigator.clipboard.writeText(effectiveWebhookUrl);
      toast.success('Webhook URL copied');
    } catch {
      toast.error('Copy failed');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center p-12">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-4 max-w-4xl" data-testid="whatsapp-settings-page">
        {/* Header */}
        <div className="flex items-start justify-between gap-2">
          <div>
            <Button
              variant="ghost"
              size="sm"
              className="mb-2 h-7 px-2"
              onClick={() => navigate('/admin/whatsapp')}
              data-testid="back-to-inbox-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back to Inbox
            </Button>
            <h1 className="text-xl lg:text-2xl font-bold flex items-center gap-2">
              <SettingsIcon className="w-6 h-6 text-green-600" />
              WhatsApp Settings
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Meta WhatsApp Business Cloud API credentials and webhook configuration.
            </p>
          </div>
        </div>

        {/* Status banner */}
        {status?.configured ? (
          <Alert className="border-green-400 bg-green-50 dark:bg-green-950/20" data-testid="status-ok">
            <CheckCircle2 className="w-4 h-4 text-green-600" />
            <AlertDescription className="text-green-800 dark:text-green-200 text-sm">
              Integration active — API version <strong>{status.api_version}</strong>
            </AlertDescription>
          </Alert>
        ) : (
          <Alert className="border-amber-400 bg-amber-50 dark:bg-amber-950/20" data-testid="status-warning">
            <AlertTriangle className="w-4 h-4 text-amber-600" />
            <AlertTitle className="text-amber-800 dark:text-amber-200">Not fully configured</AlertTitle>
            <AlertDescription className="text-amber-700 dark:text-amber-300 text-xs">
              Outbound sending is disabled until Phone Number ID and Access Token are set.
            </AlertDescription>
          </Alert>
        )}

        {/* Number-block banner (Meta rejected sends — display-name approval, etc.) */}
        {status?.number_block && (
          <Alert className="border-rose-500 bg-rose-50 dark:bg-rose-950/20" data-testid="number-block-banner">
            <AlertTriangle className="w-4 h-4 text-rose-600" />
            <AlertTitle className="text-rose-900 dark:text-rose-100">Outbound sending paused — Meta error {status.number_block.error_code}</AlertTitle>
            <AlertDescription className="text-rose-800 dark:text-rose-200 text-xs space-y-2">
              <p><strong>Meta says:</strong> {status.number_block.error_message}</p>
              {status.number_block.error_code === 131037 && (
                <div className="bg-white/60 dark:bg-black/20 rounded p-2 text-xs space-y-1">
                  <p className="font-medium">How to fix:</p>
                  <ol className="list-decimal ml-4 space-y-0.5">
                    <li>Open Meta Business Manager → WhatsApp Manager</li>
                    <li>Phone Numbers → select this number → <em>Settings</em></li>
                    <li>Under <em>Business info</em>, set / update the <strong>Display Name</strong> and submit for approval</li>
                    <li>Approval typically takes 1–3 business days</li>
                    <li>Once approved, click <em>Resume Sending</em> below</li>
                  </ol>
                </div>
              )}
              <p className="text-[11px] italic">
                Blocked at {status.number_block.blocked_at}. Sends are queued into the inbox with status <code>blocked</code> instead of being fired at Meta — your inbox won't fill with failures.
              </p>
              <Button
                variant="outline"
                size="sm"
                className="mt-2"
                onClick={async () => {
                  try {
                    await api.post('/whatsapp/config/clear-block');
                    toast.success('Block cleared — outbound sending resumed');
                    loadStatus();
                  } catch {
                    toast.error('Failed to clear block');
                  }
                }}
                data-testid="clear-number-block-btn"
              >
                Resume Sending
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* Webhook info card */}
        <Card>
          <CardHeader className="p-4">
            <CardTitle className="text-base flex items-center gap-2">
              <MessageSquare className="w-4 h-4" />
              Webhook Configuration
            </CardTitle>
            <CardDescription className="text-xs">
              Configure these values in Meta App Dashboard → WhatsApp → Configuration → Webhooks.
            </CardDescription>
          </CardHeader>
          <CardContent className="p-4 pt-0 space-y-3">
            <div>
              <Label className="text-xs">Callback URL (paste this into Meta)</Label>
              <div className="flex items-center gap-2 mt-1">
                <Input value={effectiveWebhookUrl} readOnly className="font-mono text-xs" data-testid="webhook-url-input" />
                <Button variant="outline" size="sm" onClick={copyWebhook} data-testid="copy-webhook-btn">
                  <Copy className="w-3.5 h-3.5" />
                </Button>
              </div>
              <p className="text-[11px] text-muted-foreground mt-1">
                {status?.webhook_url
                  ? <>Using your override below.</>
                  : <>Auto-derived from backend URL. Set a custom value below for production.</>
                }
              </p>
            </div>

            <div>
              <Label htmlFor="webhook_url" className="text-xs">Custom Webhook URL (override — optional)</Label>
              <Input
                id="webhook_url"
                value={form.webhook_url}
                onChange={(e) => setForm({ ...form, webhook_url: e.target.value })}
                placeholder={`${derivedWebhookUrl}`}
                className="font-mono text-xs mt-1"
                data-testid="webhook-url-override-input"
              />
              <p className="text-[11px] text-muted-foreground mt-1">
                Leave empty to auto-use <code>REACT_APP_BACKEND_URL</code>. Set this when backend and frontend live on different domains in production (e.g. <code>https://api.youracademy.com/api/whatsapp/webhook</code>).
              </p>
            </div>

            <div>
              <Label className="text-xs">Verify Token (current)</Label>
              <Input
                value={status?.verify_token || ''}
                readOnly
                className="font-mono text-xs mt-1"
                data-testid="verify-token-current"
              />
            </div>
            <div className="text-xs text-muted-foreground flex items-center gap-1">
              <ExternalLink className="w-3 h-3" />
              Webhook subscription fields: <code className="text-xs">messages</code>, <code className="text-xs">message_status</code>
            </div>
          </CardContent>
        </Card>

        {/* Credentials form */}
        <Card>
          <CardHeader className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base">Credentials</CardTitle>
                <CardDescription className="text-xs">
                  Saved values override the <code>.env</code> file. Leave a secret blank to keep the existing value.
                </CardDescription>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowSecrets((v) => !v)}
                data-testid="toggle-secrets-btn"
              >
                {showSecrets ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-4 pt-0 space-y-4">
            {/* Phone Number ID */}
            <div>
              <Label htmlFor="phone_number_id" className="text-xs">Phone Number ID</Label>
              <Input
                id="phone_number_id"
                value={form.phone_number_id}
                onChange={(e) => setForm({ ...form, phone_number_id: e.target.value })}
                placeholder="e.g. 732366896636644"
                className="font-mono text-sm mt-1"
                data-testid="phone-number-id-input"
              />
              <p className="text-[11px] text-muted-foreground mt-1">Meta App → WhatsApp → API Setup → Phone Number ID</p>
            </div>

            {/* WhatsApp Business Account ID */}
            <div>
              <Label htmlFor="business_account_id" className="text-xs">WhatsApp Business Account (WABA) ID</Label>
              <Input
                id="business_account_id"
                value={form.business_account_id}
                onChange={(e) => setForm({ ...form, business_account_id: e.target.value })}
                placeholder="e.g. 1047317367342174"
                className="font-mono text-sm mt-1"
                data-testid="business-account-id-input"
              />
              <p className="text-[11px] text-muted-foreground mt-1">
                Required to list message templates. Meta App → WhatsApp → API Setup → WhatsApp Business Account ID
              </p>
            </div>

            {/* Access Token */}
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="access_token" className="text-xs">Access Token</Label>
                {status?.has_access_token && (
                  <span className="text-[11px] text-muted-foreground">
                    Current: <code>{status.access_token_preview}</code>
                  </span>
                )}
              </div>
              <Input
                id="access_token"
                type={showSecrets ? 'text' : 'password'}
                value={form.access_token}
                onChange={(e) => setForm({ ...form, access_token: e.target.value })}
                placeholder={status?.has_access_token ? 'Leave blank to keep existing' : 'EAA… system-user permanent token'}
                className="font-mono text-sm mt-1"
                data-testid="access-token-input"
              />
              <p className="text-[11px] text-muted-foreground mt-1">Business Manager → System Users → generate token (scopes: whatsapp_business_messaging + whatsapp_business_management)</p>
            </div>

            {/* App Secret */}
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="app_secret" className="text-xs">App Secret</Label>
                {status?.has_app_secret && (
                  <span className="text-[11px] text-muted-foreground">
                    Current: <code>{status.app_secret_preview}</code>
                  </span>
                )}
              </div>
              <Input
                id="app_secret"
                type={showSecrets ? 'text' : 'password'}
                value={form.app_secret}
                onChange={(e) => setForm({ ...form, app_secret: e.target.value })}
                placeholder={status?.has_app_secret ? 'Leave blank to keep existing' : 'App Dashboard → Settings → Basic → App Secret'}
                className="font-mono text-sm mt-1"
                data-testid="app-secret-input"
              />
              <p className="text-[11px] text-muted-foreground mt-1">Used for HMAC-SHA256 webhook signature verification.</p>
            </div>

            {/* Verify Token */}
            <div>
              <Label htmlFor="verify_token" className="text-xs">Verify Token</Label>
              <Input
                id="verify_token"
                value={form.verify_token}
                onChange={(e) => setForm({ ...form, verify_token: e.target.value })}
                placeholder="Any custom string you create"
                className="font-mono text-sm mt-1"
                data-testid="verify-token-input"
              />
              <p className="text-[11px] text-muted-foreground mt-1">Must match the "Verify Token" you enter in Meta's webhook configuration.</p>
            </div>

            {/* API Version */}
            <div>
              <Label htmlFor="api_version" className="text-xs">Graph API Version</Label>
              <Input
                id="api_version"
                value={form.api_version}
                onChange={(e) => setForm({ ...form, api_version: e.target.value })}
                placeholder="v20.0"
                className="font-mono text-sm mt-1 max-w-[200px]"
                data-testid="api-version-input"
              />
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2 pt-2 flex-wrap">
              <Button onClick={save} disabled={saving} data-testid="save-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
                Save Settings
              </Button>
              <Button
                variant="outline"
                onClick={runTest}
                disabled={testing || !status?.configured}
                data-testid="test-connection-btn"
              >
                {testing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <TestTube2 className="w-4 h-4 mr-1" />}
                Test Connection
              </Button>
            </div>

            {/* Test result */}
            {testResult && (
              <Alert
                className={testResult.success
                  ? 'border-green-400 bg-green-50 dark:bg-green-950/20'
                  : 'border-red-400 bg-red-50 dark:bg-red-950/20'
                }
                data-testid="test-result"
              >
                {testResult.success ? (
                  <CheckCircle2 className="w-4 h-4 text-green-600" />
                ) : (
                  <AlertTriangle className="w-4 h-4 text-red-600" />
                )}
                <AlertDescription className="text-xs space-y-1">
                  {testResult.success ? (
                    <>
                      <div><strong>{testResult.verified_name}</strong> <Badge variant="outline" className="ml-1 text-[10px]">{testResult.code_verification_status}</Badge></div>
                      <div>Phone: {testResult.display_phone_number}</div>
                      <div>Platform: {testResult.platform_type} · Quality: {testResult.quality_rating}</div>
                    </>
                  ) : (
                    <div className="text-red-700 dark:text-red-300">
                      {testResult.error}
                      {testResult.error_code && <span className="opacity-70"> (code {testResult.error_code})</span>}
                    </div>
                  )}
                </AlertDescription>
              </Alert>
            )}

            {/* Reset options */}
            {(status?.has_access_token || status?.has_app_secret) && (
              <div className="pt-2 border-t mt-2">
                <p className="text-xs text-muted-foreground mb-2">Danger zone — clears the DB value and re-activates <code>.env</code> fallback:</p>
                <div className="flex flex-wrap gap-2">
                  {status.has_access_token && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 text-xs"
                      onClick={() => clearField('access_token')}
                      data-testid="clear-access-token-btn"
                    >
                      Clear Access Token
                    </Button>
                  )}
                  {status.has_app_secret && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 text-xs"
                      onClick={() => clearField('app_secret')}
                      data-testid="clear-app-secret-btn"
                    >
                      Clear App Secret
                    </Button>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
